import { useFindMany } from "@gadgetinc/react";
import { api } from "../../api";
import { useEffect, useState } from "react";
import LoadingSpinner from "../LoadingSpinner";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../tailwind/table";
import { Badge } from "../tailwind/badge";

export function ButtonsList({
  scenarioId,
  user,
  timeFilterSuffix,
  filterDates,
  dataStats,
}) {
  const [urlClicksEvents, setUrlClicksEvents] = useState([]);
  const [fetchingUrlClicks, setFetchingUrlClicks] = useState(false);

  useEffect(() => {
    setFetchingUrlClicks(true);
    api
      .getAllUrlClickEventBetweenDates({
        customerId: user.customerId,
        scenarioId: scenarioId,
        startDate: filterDates.startDate,
        endDate: filterDates.endDate,
      })
      .then((data) => {
        setUrlClicksEvents(data || []);
        console.log("clicks url", data);
      })
      .catch((error) => {
        console.error("Erreur lors du fetching des urlClicksEvents :", error);
        setUrlClicksEvents([]);
      })
      .finally(() => {
        setFetchingUrlClicks(false);
      });
  }, [user.customerId, scenarioId, filterDates]);

  const [clickCountsBetween, setClickCountsBetween] = useState([]);
  useEffect(() => {
    if (urlClicksEvents && urlClicksEvents.length > 0) {
      const updatedClickCounts = urlClicksEvents.reduce((acc, event) => {
        const { messageSkeleton, redirection } = event;
        const {
          id: messageSkeletonId,
          urlButtonText,
          urlButtonText2,
          whatsappTemplate,
        } = messageSkeleton || {};
        const redirectionId = redirection?.id;

        if (!messageSkeleton || !whatsappTemplate || !redirectionId) return acc;

        let buttonText = null;

        if (whatsappTemplate.redirectionUrl?.id === redirectionId) {
          buttonText = urlButtonText;
        } else if (whatsappTemplate.redirectionUrl2?.id === redirectionId) {
          buttonText = urlButtonText2;
        }

        if (!buttonText) return acc;

        const key = `${messageSkeletonId}__${buttonText}`;

        if (!acc[key]) {
          acc[key] = {
            messageSkeletonId,
            buttonText,
            count: 0,
          };
        }

        acc[key].count += 1;

        return acc;
      }, {});
      setClickCountsBetween(updatedClickCounts);
    }
  }, [urlClicksEvents]);

  const clicksArray = clickCountsBetween
    ? Object.values(clickCountsBetween)
    : [];

  const [replyButtonsBetween, setReplyButtonsBetween] = useState([]);
  const selectFields = {
    id: true,
    parentNode: {
      id: true,
    },
    scenario: {
      id: true,
    },
    replyButton: {
      title: true,
      id: true,
      ...(timeFilterSuffix !== "Between" && {
        ["clicks" + timeFilterSuffix]: true,
      }),
    },
  };

  const [{ data: replyButtonNodes, fetching: fetchingReplyButtonNodes }] =
    useFindMany(api.flowNode, {
      filter: [
        {
          scenarioId: { equals: scenarioId },
        },
        {
          type: { equals: "reply_button" },
        },
      ],
      select: selectFields,
    });

  useEffect(() => {
    if (timeFilterSuffix === "Between" && replyButtonNodes?.length > 0) {
      const replyButtonIds = replyButtonNodes.map(
        (node) => node.replyButton?.id,
      );

      api
        .getCampaignClicksBetweenDates({
          customerId: user.customerId,
          replyButtonIds: JSON.stringify(replyButtonIds),
          startDate: filterDates.startDate,
          endDate: filterDates.endDate,
        })
        .then((replyButtonsTemp) => {
          setReplyButtonsBetween(replyButtonsTemp);
        })
        .catch((error) => {
          console.error("Erreur lors de la récupération des boutons :", error);
        });
    }
  }, [timeFilterSuffix, filterDates, replyButtonNodes, user.customerId]);

  const validReplyButtonNodes =
    replyButtonNodes?.filter(
      (node) => node.replyButton?.title && node.parentNode?.id,
    ) || [];

  const getClicksForButton = (node) => {
    if (timeFilterSuffix === "Between") {
      if (!Array.isArray(replyButtonsBetween)) {
        console.warn("replyButtonsBetween n'est pas un tableau !");
        return 0;
      }

      const filteredButtons = replyButtonsBetween.filter(
        (r) =>
          r.replyButton?.flowNode?.parentNode &&
          r.replyButton?.flowNode?.id === node.id,
      );

      return filteredButtons.length;
    } else {
      return node.replyButton?.[`clicks${timeFilterSuffix}`];
    }
  };

  return (
    <>
      {(fetchingReplyButtonNodes || fetchingUrlClicks) && <LoadingSpinner />}

      <div className="flex flex-col justify-between grow">
        {!fetchingReplyButtonNodes && !fetchingUrlClicks && (
          <div>
            <Table>
              <TableHead>
                <TableRow>
                  <TableHeader>Button type</TableHeader>
                  <TableHeader className="w-[30%]">Button title</TableHeader>
                  <TableHeader className="text-center">Clicks</TableHeader>
                  <TableHeader className="text-center">Click Rate</TableHeader>
                </TableRow>
              </TableHead>
              <TableBody>
                {validReplyButtonNodes.length > 0 || clicksArray.length > 0 ? (
                  <>
                    {validReplyButtonNodes.length > 0 &&
                      validReplyButtonNodes.map((node, index) => (
                        <TableRow key={node.id} id={node.id} position={index}>
                          <TableCell>
                            <Badge color="amber">Quick reply</Badge>
                          </TableCell>
                          <TableCell>{node.replyButton.title}</TableCell>
                          <TableCell className="text-center">
                            {getClicksForButton(node)}
                          </TableCell>
                          <TableCell className="text-center">
                            {(
                              Math.min(
                                1,
                                getClicksForButton(node) /
                                  (dataStats?.[`read${timeFilterSuffix}`] || 1),
                              ) * 100
                            ).toFixed(2)}
                            %
                          </TableCell>
                        </TableRow>
                      ))}

                    {clicksArray.length > 0 &&
                      clicksArray.map((node, index) => (
                        <TableRow
                          key={node.id + index}
                          id={node.id}
                          position={index}
                        >
                          <TableCell>
                            <Badge color="violet">URL</Badge>
                          </TableCell>
                          <TableCell>{node.buttonText}</TableCell>
                          <TableCell className="text-center">
                            {node.count}
                          </TableCell>
                          <TableCell className="text-center">
                            {(
                              Math.min(
                                1,
                                node.count /
                                  (dataStats?.[`read${timeFilterSuffix}`] || 1),
                              ) * 100
                            ).toFixed(2)}
                            %
                          </TableCell>
                        </TableRow>
                      ))}
                  </>
                ) : (
                  <TableRow>
                    <TableCell colSpan={4} className="text-center">
                      No button
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </div>
        )}
      </div>
    </>
  );
}
